import React, { createContext, useContext, useState, useCallback, useMemo } from "react";
import { cartTotal } from "../utils/helpers";

const CartContext = createContext(null);

let lineSeq = 0;
const nextLineId = () => `line_${Date.now()}_${lineSeq++}`;

export function CartProvider({ children }) {
  const [orderType, setOrderType] = useState("dine_in"); // 'dine_in' | 'delivery' | 'take_away'
  const [table, setTable] = useState(null); // { id, name, areaName }
  const [deliveryAddress, setDeliveryAddress] = useState("");
  const [contactNumber, setContactNumber] = useState("");
  const [cart, setCart] = useState([]);
  const [orderNote, setOrderNote] = useState("");
  const [noOfPeople, setNoOfPeople] = useState(1);

  const addItem = useCallback((product, option, addons, quantity, notes) => {
    setCart((prev) => [
      ...prev,
      { cartId: nextLineId(), product, option: option || null, addons: addons || [], quantity: quantity || 1, notes: notes || "" },
    ]);
  }, []);

  const updateQuantity = useCallback((cartId, quantity) => {
    setCart((prev) =>
      quantity <= 0
        ? prev.filter((i) => i.cartId !== cartId)
        : prev.map((i) => (i.cartId === cartId ? { ...i, quantity } : i))
    );
  }, []);

  const removeItem = useCallback((cartId) => {
    setCart((prev) => prev.filter((i) => i.cartId !== cartId));
  }, []);

  const clearCart = useCallback(() => {
    setCart([]);
    setOrderNote("");
    setNoOfPeople(1);
    setDeliveryAddress("");
    setContactNumber("");
    // orderType/table deliberately kept — a waiter serving the same table
    // for a follow-up round shouldn't have to reselect it every time.
  }, []);

  const itemCount = useMemo(() => cart.reduce((n, i) => n + i.quantity, 0), [cart]);
  const total = useMemo(() => cartTotal(cart), [cart]);

  return (
    <CartContext.Provider
      value={{
        orderType,
        setOrderType,
        table,
        setTable,
        deliveryAddress,
        setDeliveryAddress,
        contactNumber,
        setContactNumber,
        cart,
        addItem,
        updateQuantity,
        removeItem,
        clearCart,
        orderNote,
        setOrderNote,
        noOfPeople,
        setNoOfPeople,
        itemCount,
        total,
      }}
    >
      {children}
    </CartContext.Provider>
  );
}

export function useCart() {
  const ctx = useContext(CartContext);
  if (!ctx) throw new Error("useCart must be used within CartProvider");
  return ctx;
}
